// src/pages/WeightDiary.tsx
import React, { useState, useEffect } from 'react';
import { Card, Form, Button, InputNumber, Table, message, Space, Typography, Modal, DatePicker } from 'antd';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import { diaryAPI } from '../utils/api';
import dayjs from 'dayjs';
import { decimalInputNumberProps, formatDecimalDisplay } from '../utils/decimalFormat';
import { formatDiaryDateTime, sortDiaryEntriesNewestFirst } from '../utils/diaryDisplay';

const { Title } = Typography;

export const WeightDiary: React.FC = () => {
  const [form] = Form.useForm();
  const [entries, setEntries] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  useEffect(() => {
    loadEntries();
  }, []);

  const normalizeDateValue = (value: any): string | undefined => {
    if (!value) return undefined;
    if (typeof value === 'string') return value;
    if (value?.format) return value.format('YYYY-MM-DD');
    return undefined;
  };

  const loadEntries = async () => {
    try {
      setLoading(true);
      const response = await diaryAPI.getWeightEntries();
      setEntries(sortDiaryEntriesNewestFirst((response.data || []) as any[]));
    } catch (error: any) {
      console.error('Error loading weight entries:', error);
      message.error('Ошибка загрузки записей веса');
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setModalVisible(false);
    setEditingId(null);
    form.resetFields();
  };

  const openCreateModal = () => {
    setEditingId(null);
    form.setFieldsValue({ entry_date: dayjs() });
    setModalVisible(true);
  };

  const handleEdit = (record: any) => {
    setEditingId(record.id);
    form.setFieldsValue({
      weight_kg: record.weight_kg,
      entry_date: record.entry_date ? dayjs(record.entry_date) : dayjs(),
    });
    setModalVisible(true);
  };

  const onFinish = async (values: any) => {
    try {
      const data = {
        weight_kg: values.weight_kg,
        entry_date: normalizeDateValue(values.entry_date) || dayjs().format('YYYY-MM-DD'),
      };
      if (editingId) {
        await diaryAPI.updateWeightEntry(editingId, data);
        message.success('Вес успешно обновлен!');
      } else {
        await diaryAPI.createWeightEntry(data);
        message.success('Вес успешно добавлен!');
      }
      closeModal();
      await loadEntries();
    } catch (error: any) {
      console.error('Error saving weight entry:', error);
      message.error(error.response?.data?.detail || 'Ошибка сохранения веса');
    }
  };

  const columns = [
    {
      title: 'Дата и время',
      key: 'datetime',
      render: (_: any, record: any) => formatDiaryDateTime(record),
    },
    {
      title: 'Вес (кг)',
      dataIndex: 'weight_kg',
      key: 'weight_kg',
      render: (value: number) => `${formatDecimalDisplay(value)} кг`,
    },
    {
      title: 'Действия',
      key: 'actions',
      render: (_: any, record: any) => (
        <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} size="small" />
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ color: '#263238' }}>Дневник массы тела</Title>
      </div>

      <Card style={{ borderRadius: '12px', marginBottom: 24 }}>
        <Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={openCreateModal}
            style={{ backgroundColor: '#7CB342', borderColor: '#7CB342' }}
          >
            Добавить запись
          </Button>
        </Space>
      </Card>

      <Card style={{ borderRadius: '12px' }}>
        <Table
          columns={columns}
          dataSource={entries}
          loading={loading}
          rowKey="id"
          pagination={{ pageSize: 20 }}
        />
      </Card>

      <Modal
        title={editingId ? 'Редактировать запись веса' : 'Добавить запись веса'}
        open={modalVisible}
        onCancel={closeModal}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            label="Вес (кг)"
            name="weight_kg"
            rules={[{ required: true, message: 'Введите вес!' }]}
          >
            <InputNumber
              placeholder="Введите вес"
              min={30}
              max={200}
              precision={1}
              step={0.1}
              style={{ width: '100%' }}
              size="large"
              {...decimalInputNumberProps}
            />
          </Form.Item>

          <Form.Item
            label="Дата"
            name="entry_date"
            initialValue={dayjs()}
          >
            <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" size="large" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" style={{ backgroundColor: '#7CB342', borderColor: '#7CB342' }}>
                {editingId ? 'Обновить' : 'Сохранить'}
              </Button>
              <Button onClick={closeModal}>
                Отмена
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
